import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Markdown } from "@/components/ui/markdown";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BibleVersePicker } from "@/components/scripture/BibleVersePicker";
import { ScriptureReferencePanel } from "@/components/scripture/ScriptureReferencePanel";
import { ScriptureNotesList } from "@/components/scripture/ScriptureNotesList";
import { NotesSidebar } from "@/components/NotesSidebar";
import { AIResearch } from "@/components/editor/AIResearch";
import { Quote } from "@/types";
import { get, post } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Plus } from "lucide-react";

export type ResearchTab = "scripture" | "quotes" | "notes" | "outline" | "ai";

interface HeadingItem {
  text: string;
  level: number;
}

interface ResearchPaneProps {
  isOpen: boolean;
  onClose: () => void;
  tab: ResearchTab;
  onTabChange: (tab: ResearchTab) => void;
  documentId: number;
  documentTitle: string;
  headings: HeadingItem[];
  onInsertScripture: (payload: { reference: string; text: string }) => void;
  onInsertQuote: (quote: Quote) => void;
  onInsertNote: (content: string) => void;
  onJumpToHeading: (text: string) => void;
}

export function ResearchPane({
  isOpen,
  onClose,
  tab,
  onTabChange,
  documentId,
  documentTitle,
  headings,
  onInsertScripture,
  onInsertQuote,
  onInsertNote,
  onJumpToHeading,
}: ResearchPaneProps) {
  const [scriptureInner, setScriptureInner] = useState("search");
  const [scriptureNotesRefresh, setScriptureNotesRefresh] = useState(0);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [quoteFilter, setQuoteFilter] = useState("");
  const [isAddingQuote, setIsAddingQuote] = useState(false);
  const [newQuote, setNewQuote] = useState({
    content: "",
    author: "",
    source: "",
  });

  useEffect(() => {
    if (!isOpen || tab !== "quotes") return;
    get<Quote[]>("/quotes")
      .then((data) => setQuotes(data || []))
      .catch(() => setQuotes([]));
  }, [isOpen, tab]);

  const handleAddQuote = async () => {
    if (!newQuote.content || !newQuote.author) return;
    try {
      const data = await post<Quote>("/quotes", {
        content: newQuote.content,
        author: newQuote.author,
        source: newQuote.source || null,
      });
      setQuotes([data, ...quotes]);
      setNewQuote({ content: "", author: "", source: "" });
      setIsAddingQuote(false);
    } catch (error) {
      console.error("Error adding quote:", error);
    }
  };

  const filteredQuotes = quotes.filter((q) => {
    const hay = `${q.content} ${q.author} ${q.source || ""}`.toLowerCase();
    return hay.includes(quoteFilter.toLowerCase());
  });

  if (!isOpen) return null;

  return (
    <aside className="fixed inset-y-0 right-0 z-40 w-full max-w-md bg-card border-l border-border shadow-lg flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
        <div>
          <h2 className="font-semibold">Research</h2>
          <p className="text-xs text-muted-foreground truncate max-w-[16rem]">
            {documentTitle}
          </p>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} title="Close (Esc)">
          <X className="h-5 w-5" />
        </Button>
      </div>

      <Tabs
        value={tab}
        onValueChange={(v) => onTabChange(v as ResearchTab)}
        className="flex flex-col flex-1 min-h-0"
      >
        <div className="px-3 pt-3 shrink-0">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="scripture">Scripture</TabsTrigger>
            <TabsTrigger value="quotes">Quotes</TabsTrigger>
            <TabsTrigger value="notes">Notes</TabsTrigger>
            <TabsTrigger value="outline">Outline</TabsTrigger>
            <TabsTrigger value="ai">Research</TabsTrigger>
          </TabsList>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <TabsContent value="scripture" className="mt-0 space-y-4">
            <Tabs value={scriptureInner} onValueChange={setScriptureInner}>
              <TabsList className="grid grid-cols-3 mb-4">
                <TabsTrigger value="search">Look up</TabsTrigger>
                <TabsTrigger value="saved">Saved</TabsTrigger>
                <TabsTrigger value="notes">Notes</TabsTrigger>
              </TabsList>
              <TabsContent value="search">
                <p className="text-xs text-muted-foreground mb-3">
                  Look up any passage via YouVersion — NIV by default.
                </p>
                <BibleVersePicker
                  documentId={documentId}
                  onVerseSelect={() => setScriptureInner("saved")}
                  onInsert={onInsertScripture}
                />
              </TabsContent>
              <TabsContent value="saved">
                <ScriptureReferencePanel
                  documentId={documentId}
                  onInsert={onInsertScripture}
                  onNoteSaved={() => {
                    setScriptureNotesRefresh((n) => n + 1);
                    setScriptureInner("notes");
                  }}
                />
              </TabsContent>
              <TabsContent value="notes">
                <ScriptureNotesList
                  documentId={documentId}
                  refreshTrigger={scriptureNotesRefresh}
                />
              </TabsContent>
            </Tabs>
          </TabsContent>

          <TabsContent value="quotes" className="mt-0 space-y-3">
            <div className="flex gap-2">
              <Input
                placeholder="Filter quotes…"
                value={quoteFilter}
                onChange={(e) => setQuoteFilter(e.target.value)}
              />
              <Button
                size="icon"
                variant="outline"
                onClick={() => setIsAddingQuote((v) => !v)}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {isAddingQuote && (
              <Card className="p-3 space-y-2">
                <Textarea
                  placeholder="Quote content…"
                  value={newQuote.content}
                  onChange={(e) =>
                    setNewQuote({ ...newQuote, content: e.target.value })
                  }
                />
                <Input
                  placeholder="Author"
                  value={newQuote.author}
                  onChange={(e) =>
                    setNewQuote({ ...newQuote, author: e.target.value })
                  }
                />
                <Input
                  placeholder="Source (optional)"
                  value={newQuote.source}
                  onChange={(e) =>
                    setNewQuote({ ...newQuote, source: e.target.value })
                  }
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleAddQuote}>
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setIsAddingQuote(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </Card>
            )}

            {filteredQuotes.map((quote) => (
              <Card key={quote.id} className="p-3">
                <p className="text-sm line-clamp-4">
                  "<Markdown content={quote.content} inline />"
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  — {quote.author}
                  {quote.source ? `, ${quote.source}` : ""}
                </p>
                <Button
                  size="sm"
                  variant="secondary"
                  className="mt-2"
                  onClick={() => onInsertQuote(quote)}
                >
                  Insert at cursor
                </Button>
              </Card>
            ))}
            {filteredQuotes.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-8">
                No quotes match.
              </p>
            )}
          </TabsContent>

          <TabsContent value="notes" className="mt-0">
            <NotesSidebar
              embedded
              parentType="document"
              parentId={documentId}
              title={documentTitle}
              onInsert={onInsertNote}
            />
          </TabsContent>

          <TabsContent value="outline" className="mt-0">
            {headings.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                Add headings (# Heading) to build an outline.
              </p>
            ) : (
              <ul className="space-y-1">
                {headings.map((h, i) => (
                  <li key={`${h.text}-${i}`}>
                    <button
                      type="button"
                      className="w-full text-left text-sm py-1.5 px-2 rounded-md hover:bg-muted"
                      style={{ paddingLeft: `${(h.level - 1) * 12 + 8}px` }}
                      onClick={() => onJumpToHeading(h.text)}
                    >
                      {h.text}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>

          <TabsContent value="ai" className="mt-0">
            <AIResearch documentId={documentId} />
          </TabsContent>
        </div>
      </Tabs>
    </aside>
  );
}
